import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../lib/haptic';
import { resizeImageFile } from '../../lib/photo';

type Props = {
  value?: string | null;
  onChange: (dataUrl: string | null) => void;
  label?: string;
};

export function PhotoCapture({ value, onChange, label }: Props) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await resizeImageFile(file);
      haptic.select();
      onChange(dataUrl);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : null;
      setError(msg ?? t('photo_capture_failed'));
    } finally {
      setBusy(false);
      // Allow the same camera/file result to be selected for a subsequent
      // expense after the parent clears the form.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[var(--color-text)]">
        {label ?? t('photo_capture_label')}
      </label>
      {value ? (
        <div className="relative">
          <img src={value} alt={t('photo_capture_preview_alt')} className="w-full rounded-2xl border border-[var(--color-border,#d1d5db)]" />
          <button
            type="button"
            onClick={() => { haptic.tap(); onChange(null); }}
            className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1 rounded-full"
          >
            {t('clear')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { haptic.tap(); inputRef.current?.click(); }}
          disabled={busy}
          className="w-full py-6 border-2 border-dashed border-[var(--color-border,#d1d5db)] rounded-2xl text-sm text-[var(--color-text-secondary)] disabled:opacity-50"
        >
          {busy ? t('photo_capture_processing') : t('photo_capture_tap_to_capture')}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onFile(file);
        }}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
